// DR-017 SuperAdmin owner-plane MFA view — ENTITLEMENT + COMPLIANCE ONLY (read-only).
//
// Governance boundary correction: SuperAdmin owns the MFA *capability* (granted via the tenant's
// plan / add-on / override in the commercial plane) and may view a safe adoption/compliance summary.
// SuperAdmin does NOT manage a tenant's day-to-day MFA policy (enforcement mode, targeted roles, grace
// or assurance durations, recovery, or per-staff factor resets) — that authority belongs to the tenant
// Admin / IT-Integration via the Business plane. This panel is therefore read-only: it surfaces the
// safe policy status + compliance so the platform owner has visibility, with no editing controls.
// It never shows a factor secret, otpauth URI, recovery code, assurance credential, or raw id.
import React, { useEffect, useState } from 'react';

const BACKEND_URL = process.env.REACT_APP_API_BASE || process.env.REACT_APP_BACKEND_URL || '';

function saToken() {
  return localStorage.getItem('superadmin_session') || localStorage.getItem('superadmin_session_token')
    || localStorage.getItem('superAdminToken') || localStorage.getItem('superadminToken');
}

async function saFetch(path) {
  const res = await fetch(`${BACKEND_URL}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${saToken()}` },
  });
  let data = null; try { data = await res.json(); } catch (_) { data = {}; }
  return { ok: res.ok, status: res.status, data };
}

export default function TenantMfaPolicyPanel({ businessId, onManageFeature }) {
  const [policy, setPolicy] = useState(null);
  const [compliance, setCompliance] = useState(null);
  const [capability, setCapability] = useState('');
  const [effective, setEffective] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { if (businessId) load(); /* eslint-disable-next-line */ }, [businessId]);

  async function load() {
    setError('');
    const r = await saFetch(`/api/v1/superadmin/tenants/${encodeURIComponent(businessId)}/mfa-policy`);
    setLoaded(true);
    if (!r.ok) { setError('Could not load MFA summary.'); return; }
    setPolicy(r.data.policy || null);
    setCompliance(r.data.compliance || null);
    setCapability(r.data.capability || '');
    setEffective(r.data.effective_policy_status || '');
  }

  if (!loaded) return <div className="p-4 text-sm text-gray-500">Loading MFA summary…</div>;

  const status = policy?.status || 'not_configured';
  const enrolled = compliance?.enrolled_factors ?? 0;
  const active = compliance?.active_staff ?? 0;
  const pct = active ? Math.round((enrolled / active) * 100) : 0;
  const entitled = capability === 'MFA_CAPABILITY_AVAILABLE';

  return (
    <div className="p-4 max-h-[80vh] overflow-y-auto" style={{ maxWidth: 640 }}>
      <h3 className="text-lg font-semibold">Multi-factor authentication</h3>
      <div className="mt-2 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
        MFA is a <b>Features</b> capability. Grant or deny it for this tenant on the <b>Features</b> tab
        using the normal Grant / Deny workflow. Day-to-day policy (enforcement, targeted roles, durations,
        staff factor resets) is managed by the tenant’s <b>Admin / IT-Integration</b>, not here.
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
        <div className="border rounded-md p-3">
          <div className="text-gray-500">Capability entitlement</div>
          <div className={`font-medium ${entitled ? 'text-green-700' : 'text-gray-700'}`}>{entitled ? 'Enabled' : 'Not enabled'}</div>
        </div>
        <div className="border rounded-md p-3">
          <div className="text-gray-500">Effective policy</div>
          <div className="font-medium capitalize">{String(effective || 'not configured').replace(/_/g, ' ')}</div>
        </div>
        <div className="border rounded-md p-3">
          <div className="text-gray-500">Stored policy status</div>
          <div className="font-medium capitalize">{status.replace(/_/g, ' ')}</div>
        </div>
        <div className="border rounded-md p-3">
          <div className="text-gray-500">Policy version</div>
          <div className="font-medium">{policy?.policy_version ?? '—'}</div>
        </div>
        <div className="border rounded-md p-3 col-span-2">
          <div className="text-gray-500">Adoption (safe summary)</div>
          <div className="font-medium">{enrolled} of {active} active staff enrolled ({pct}%)</div>
        </div>
      </div>

      {onManageFeature && (
        <button onClick={onManageFeature}
          className="mt-4 inline-flex items-center gap-2 rounded-md bg-blue-600 text-white px-4 py-2 text-sm">
          Manage MFA capability in Features
        </button>
      )}

      <p className="mt-3 text-xs text-gray-400">
        Read-only. Enforcement and targeted roles are set by the tenant Admin/IT in Business Settings →
        Security. Secrets, QR codes and recovery codes are never shown here.
      </p>
      {error && <p className="mt-2 text-sm text-red-600" role="alert">{error}</p>}
    </div>
  );
}
